// Shared validation + parsing for the `number` runtime question card.
//
// Single source of truth so the card's inline feedback and the engine's
// gate (QuizRuntime._isAnswered, progress fill + required gating) can't
// drift. Mirrors shortTextValidation.js. Validation is declared by the
// question's input config; the server re-checks on submit once
// persistence exists (see docs/SPEC_number_question_card.md).
//
// Input config lives on the question content as `content.input`:
//   { required, min, max, decimals, unit (localized) }

/** Read and normalize the input config off a resolved engine item. */
export function numberConfig(item) {
  const input = (item && item.item && item.item.content && item.item.content.input) || {};
  return {
    required: input.required === true,
    min: _numOrNull(input.min),
    max: _numOrNull(input.max),
    decimals: Number.isInteger(input.decimals) && input.decimals >= 0 ? input.decimals : 0,
    unit: (input.unit && typeof input.unit === 'object') ? input.unit : null,
  };
}

function _numOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Normalize a raw user string to a canonical numeric string (or null if it
// isn't a number), handling Swiss/European separators:
//   - spaces and apostrophes (' ’) are thousands separators → stripped
//     (Swiss convention: 1'234.50)
//   - if both '.' and ',' appear, the LAST one is the decimal separator and
//     the other is grouping  (123,456.78 → 123456.78; 1.234,56 → 1234.56)
//   - a lone comma is the decimal separator (12,5 → 12.5)
//   - multiple commas (or multiple dots) with no other separator are all
//     grouping (1,234,567 → 1234567)
// Residual ambiguity: a lone "1,234" parses as 1.234. The decimals rule is
// the backstop — with decimals:0 that value is rejected as too precise.
export function normalizeNumberString(raw) {
  if (typeof raw !== 'string') return null;
  let s = raw.trim();
  if (!s) return null;
  // Strip thousands separators: spaces (incl. non-breaking) and apostrophes.
  s = s.replace(/[\s '’]/g, '');

  const neg = s.startsWith('-');
  if (neg) s = s.slice(1);

  const nDot = (s.match(/\./g) || []).length;
  const nComma = (s.match(/,/g) || []).length;

  let decimalSep = null;
  if (nDot && nComma) {
    decimalSep = s.lastIndexOf('.') > s.lastIndexOf(',') ? '.' : ',';
  } else if (nComma) {
    decimalSep = nComma === 1 ? ',' : null;   // multiple commas → all grouping
  } else if (nDot) {
    decimalSep = nDot === 1 ? '.' : null;     // multiple dots → all grouping
  }

  if (decimalSep === '.') {
    s = s.replace(/,/g, '');
  } else if (decimalSep === ',') {
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    s = s.replace(/[.,]/g, '');
  }

  if (neg) s = `-${s}`;
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  return s;
}

// True if `num` carries more precision than `decimals` allows — checked by
// value (is num × 10^decimals a whole number?), not by counting typed
// characters. So 2.0 passes when decimals=0 but 2.5 does not. Never rounds.
function _exceedsDecimals(num, decimals) {
  const scaled = num * (10 ** decimals);
  return Math.abs(scaled - Math.round(scaled)) > 1e-9;
}

/**
 * Evaluate a raw string against a normalized config.
 *
 *   error:    'required' | 'not_number' | 'decimals' | 'min' | 'max' | null
 *   valid:    the field may be left as-is (empty optional is valid)
 *   answered: a present, fully-valid number (drives progress + required gate)
 *   value:    the parsed number when the string parses (even if out of
 *             range), else null — this is what the card emits as state.value
 */
export function evaluateNumber(raw, cfg) {
  const s = typeof raw === 'string' ? raw : (raw == null ? '' : String(raw));
  if (s.trim().length === 0) {
    return { error: cfg.required ? 'required' : null, valid: !cfg.required, answered: false, value: null };
  }

  const normalized = normalizeNumberString(s);
  const num = normalized === null ? NaN : Number(normalized);
  if (!Number.isFinite(num)) {
    return { error: 'not_number', valid: false, answered: false, value: null };
  }
  if (_exceedsDecimals(num, cfg.decimals)) {
    return { error: 'decimals', valid: false, answered: false, value: num };
  }
  if (cfg.min != null && num < cfg.min) {
    return { error: 'min', valid: false, answered: false, value: num };
  }
  if (cfg.max != null && num > cfg.max) {
    return { error: 'max', valid: false, answered: false, value: num };
  }
  return { error: null, valid: true, answered: true, value: num };
}

/**
 * Is an already-parsed number acceptable under this config?
 *
 * The value-level half of evaluateNumber(), split out because callers that
 * hold a number (rather than the raw string someone typed) must not have to
 * stringify it to re-enter the parser: String(1e21) is "1e+21", which the
 * normalizer rightly rejects. Shared with the numeric_grid card so a grid
 * row and a standalone number question judge precision and range alike.
 */
export function isNumberValueValid(num, cfg) {
  if (typeof num !== 'number' || !Number.isFinite(num)) return false;
  const decimals = Number.isInteger(cfg.decimals) && cfg.decimals >= 0 ? cfg.decimals : 0;
  if (_exceedsDecimals(num, decimals)) return false;
  if (cfg.min != null && num < cfg.min) return false;
  if (cfg.max != null && num > cfg.max) return false;
  return true;
}

/**
 * Engine convenience: is this item's number answer present and valid?
 * Works from the emitted state.value (a number) directly — decimals and
 * range are value-based, so no raw string is needed.
 */
export function isNumberAnswered(item, state) {
  if (!state || typeof state.value !== 'number' || !Number.isFinite(state.value)) return false;
  return isNumberValueValid(state.value, numberConfig(item));
}
